// Metronome engine with accurate lookahead scheduling against the AudioContext clock.
//
// setInterval alone drifts and is subject to tab/background throttling, so beat
// audio is scheduled ahead of time using AudioContext.currentTime (the "scheduler
// look-ahead" pattern) — up to SCHEDULE_AHEAD_SECONDS before it's actually
// audible. Each emitted 'beat' event carries that real scheduledTime (an
// AudioContext timestamp, not "now"), so a visual listener must delay its own
// flash until that moment arrives (see ui/beatVisual.ts's scheduleBeatFlash) —
// reacting to the event immediately would show the beat up to 200ms before you
// hear it.
//
// Every click/accent sound is synthesized as a stereo AudioBuffer whose LEFT
// channel is explicit zero-filled silence and whose RIGHT channel carries the
// tone (see stereoRouting.buildStereoClickBuffer). The buffer is played through
// an explicit splitter/merger passthrough, never through a plain mono connection
// that Web Audio could implicitly duplicate to both output channels.

import type { MetronomeSettings } from '../core/types';
import { buildSelectableStereoClickBuffer, createExplicitStereoPassthrough } from './stereoRouting';

const SCHEDULE_AHEAD_SECONDS = 0.2;
const SCHEDULER_INTERVAL_MS = 25;

export interface BeatEvent {
  beatIndexInMeasure: number; // 0-based
  isAccent: boolean;
  isSubdivision: boolean; // true if this tick is a subdivision, not the main beat
  /** Which subdivision slot within the beat this is: 0 is always the main
   * beat itself; 1, 2... are the "and"s between it and the next main beat
   * (e.g. subdivision=2 means exactly one subdivision event per beat, at
   * index 1 — the single "and"). Lets a visual light up a DIFFERENT
   * indicator per subdivision slot (not just "some subdivision happened"),
   * matching however many subdivisions the current settings actually use. */
  subdivisionIndex: number;
  isCountIn: boolean;
  scheduledTime: number; // AudioContext time
}

export type MetronomeEvent =
  | { type: 'beat'; beat: BeatEvent }
  | { type: 'count-in-complete' }
  | { type: 'stopped' };

export class MetronomeEngine {
  private audioContext: AudioContext;
  private gainNode: GainNode;
  private schedulerTimer: ReturnType<typeof setInterval> | null = null;
  private nextBeatTime = 0;
  private beatCounterInMeasure = 0;
  private isCountingIn = false;
  private countInMeasuresRemaining = 0;
  private running = false;
  private listeners = new Set<(event: MetronomeEvent) => void>();
  private settings: MetronomeSettings;
  private scheduledSources: AudioBufferSourceNode[] = [];

  constructor(audioContext: AudioContext, settings: MetronomeSettings) {
    this.audioContext = audioContext;
    this.settings = settings;
    this.gainNode = audioContext.createGain();
    this.gainNode.gain.value = settings.clickVolume;
    const route = createExplicitStereoPassthrough(audioContext);
    this.gainNode.connect(route.input);
    route.output.connect(audioContext.destination);
  }

  subscribe(listener: (event: MetronomeEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(event: MetronomeEvent): void {
    for (const listener of this.listeners) listener(event);
  }

  updateSettings(settings: MetronomeSettings): void {
    this.settings = settings;
    this.gainNode.gain.value = settings.clickVolume;
  }

  get isRunning(): boolean {
    return this.running;
  }

  async start(): Promise<void> {
    if (this.running) return;
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
    this.running = true;
    this.beatCounterInMeasure = 0;
    this.isCountingIn = this.settings.countInMeasures > 0;
    this.countInMeasuresRemaining = this.settings.countInMeasures;
    this.nextBeatTime = this.audioContext.currentTime + 0.1;
    this.schedulerTimer = setInterval(() => this.schedulerTick(), SCHEDULER_INTERVAL_MS);
  }

  stop(): void {
    this.running = false;
    if (this.schedulerTimer !== null) {
      clearInterval(this.schedulerTimer);
      this.schedulerTimer = null;
    }
    for (const source of this.scheduledSources) {
      try {
        source.stop();
      } catch {
        // Already stopped; ignore.
      }
    }
    this.scheduledSources = [];
    this.emit({ type: 'stopped' });
  }

  /** Pause: stop scheduling and audio, but a caller may choose to resume from scratch (no mid-measure resume). */
  pause(): void {
    this.stop();
  }

  private secondsPerBeat(): number {
    // beatUnit 4 = quarter-note gets the beat; beatUnit 8 = eighth-note gets the beat (e.g. 6/8).
    const quarterNoteSeconds = 60 / this.settings.bpm;
    return this.settings.beatUnit === 8 ? quarterNoteSeconds / 2 : quarterNoteSeconds;
  }

  private schedulerTick(): void {
    // A backgrounded tab can throttle/pause this interval for far longer
    // than SCHEDULE_AHEAD_SECONDS while AudioContext.currentTime keeps
    // advancing in real time regardless — without this guard, the very
    // first tick after returning would otherwise need to loop through and
    // schedule EVERY beat missed during that whole gap in one synchronous
    // burst (potentially hundreds of AudioBuffer allocations/scheduling
    // calls), which is exactly the kind of multi-second main-thread block
    // that trips the browser's own "Page Unresponsive" watchdog. Silently
    // fast-forward past anything more than one beat behind instead: those
    // beats were never audible anyway (the tab wasn't focused), so nothing
    // is lost by skipping straight to the next one actually worth playing.
    const beatDuration = this.secondsPerBeat();
    const beatsBehind = Math.floor((this.audioContext.currentTime - this.nextBeatTime) / beatDuration);
    if (beatsBehind > 1) this.fastForwardBeats(beatsBehind);

    while (this.nextBeatTime < this.audioContext.currentTime + SCHEDULE_AHEAD_SECONDS) {
      this.scheduleBeat(this.nextBeatTime);
      this.advanceBeat();
    }
  }

  /** Jumps nextBeatTime forward by exactly `count` beats in one step,
   * correctly advancing beatCounterInMeasure/countInMeasuresRemaining by
   * the same count (via modular arithmetic) rather than resetting them —
   * so the beat grid and measure position stay correct after the jump,
   * exactly as if advanceBeat() had genuinely been called `count` times,
   * just without the O(count) loop or any of the skipped beats actually
   * being scheduled/audible. */
  private fastForwardBeats(count: number): void {
    this.nextBeatTime += count * this.secondsPerBeat();
    if (this.isCountingIn) {
      const totalCountInBeats = this.countInMeasuresRemaining * this.settings.beatsPerMeasure - this.beatCounterInMeasure;
      if (count >= totalCountInBeats) {
        // The whole rest of the count-in was skipped while backgrounded —
        // land exactly where advanceBeat() would have: count-in finished,
        // now on the beat-in-measure the leftover beats land on.
        this.isCountingIn = false;
        this.countInMeasuresRemaining = 0;
        this.beatCounterInMeasure = (count - totalCountInBeats) % this.settings.beatsPerMeasure;
        this.emit({ type: 'count-in-complete' });
        return;
      }
    }
    const totalBeatsInMeasure = this.beatCounterInMeasure + count;
    this.beatCounterInMeasure = totalBeatsInMeasure % this.settings.beatsPerMeasure;
    const measuresAdvanced = Math.floor(totalBeatsInMeasure / this.settings.beatsPerMeasure);
    if (this.isCountingIn) this.countInMeasuresRemaining -= measuresAdvanced;
  }

  private scheduleBeat(time: number): void {
    const isAccent = !this.isCountingIn && this.settings.accentFirstBeat && this.beatCounterInMeasure === 0;
    const beatDuration = this.secondsPerBeat();
    const subdivisionCount = this.settings.subdivision;

    for (let subdivisionIndex = 0; subdivisionIndex < subdivisionCount; subdivisionIndex++) {
      const subdivisionTime = time + (subdivisionIndex * beatDuration) / subdivisionCount;
      const isMainBeat = subdivisionIndex === 0;

      const buffer = buildSelectableStereoClickBuffer(this.audioContext, {
        sound: this.settings.sound,
        isAccent: isMainBeat && isAccent,
        isSubdivision: !isMainBeat,
        sampleRate: this.audioContext.sampleRate,
        gain: 1,
        channel: this.settings.clickChannel,
      });
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(this.gainNode);
      source.start(subdivisionTime);
      source.onended = () => {
        this.scheduledSources = this.scheduledSources.filter((s) => s !== source);
      };
      this.scheduledSources.push(source);

      // One 'beat' event per ACTUAL audible tick (main beat AND every
      // subdivision), each carrying its own real scheduledTime — previously
      // this only ever emitted once per main beat, so a subdivision tick was
      // audible but had no matching event a visual could react to at all.
      this.emit({
        type: 'beat',
        beat: {
          beatIndexInMeasure: this.beatCounterInMeasure,
          isAccent: isMainBeat && isAccent,
          isSubdivision: !isMainBeat,
          subdivisionIndex,
          isCountIn: this.isCountingIn,
          scheduledTime: subdivisionTime,
        },
      });
    }
  }

  private advanceBeat(): void {
    this.nextBeatTime += this.secondsPerBeat();
    this.beatCounterInMeasure++;
    if (this.beatCounterInMeasure >= this.settings.beatsPerMeasure) {
      this.beatCounterInMeasure = 0;
      if (this.isCountingIn) {
        this.countInMeasuresRemaining--;
        if (this.countInMeasuresRemaining <= 0) {
          this.isCountingIn = false;
          this.emit({ type: 'count-in-complete' });
        }
      }
    }
  }

  teardown(): void {
    this.stop();
    this.gainNode.disconnect();
  }
}

/** Tap-tempo helper: feed successive tap timestamps (ms), get back a BPM estimate. */
export class TapTempo {
  private taps: number[] = [];
  private readonly maxIntervalMs = 2500; // taps slower than this reset the sequence

  tap(nowMs: number): number | null {
    if (this.taps.length > 0) {
      const last = this.taps[this.taps.length - 1];
      if (nowMs - last > this.maxIntervalMs) {
        this.taps = [];
      }
    }
    this.taps.push(nowMs);
    if (this.taps.length > 8) this.taps.shift();
    if (this.taps.length < 2) return null;

    const intervals: number[] = [];
    for (let i = 1; i < this.taps.length; i++) {
      intervals.push(this.taps[i] - this.taps[i - 1]);
    }
    const averageMs = intervals.reduce((a, b) => a + b, 0) / intervals.length;
    const bpm = 60000 / averageMs;
    return Math.round(Math.min(300, Math.max(30, bpm)));
  }

  reset(): void {
    this.taps = [];
  }
}
